import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, CheckCircle2, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCompany } from '../lib/api';
import { BackLink, CardShell, EmptyState, LoadingScreen, PhoneText, SectionTag, TealButton } from '../components/common/BrandElements';

const CompanyDetailPage = () => {
  const { slug } = useParams();
  const { t, isRTL } = useLanguage();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getCompany(slug);
        setCompany(res.data);
      } catch (error) {
        console.error('Error loading company:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  if (loading) return <LoadingScreen />;

  if (!company) {
    return (
      <div className="min-h-screen bg-raad-porcelain pt-40">
        <EmptyState
          title={t('الشركة غير موجودة', 'Company Not Found')}
          action={<TealButton to="/companies">{t('العودة للشركات', 'Back to Companies')}</TealButton>}
        />
      </div>
    );
  }

  const services = (isRTL ? company.services_ar : company.services_en) || [];

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="company-detail-page">
      <section className="relative overflow-hidden pt-36 pb-16 lg:pt-40">
        <div className="brand-grid absolute inset-0 opacity-55" />
        <div className="absolute -top-28 end-[-10rem] h-80 w-80 rounded-full bg-raad-teal/12 blur-3xl" />
        <div className="container relative z-10 mx-auto px-6">
          <BackLink to="/companies" label={t('العودة للشركات', 'Back to Companies')} isRTL={isRTL} />

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-10 grid gap-8 lg:grid-cols-[auto_1fr] lg:items-center">
            <div className="flex h-28 w-28 items-center justify-center rounded-[2rem] border border-raad-teal/15 bg-white shadow-soft">
              {company.logo_url ? (
                <img src={company.logo_url} alt={t(company.name_ar, company.name_en)} className="h-20 w-20 object-contain" />
              ) : (
                <span className="text-5xl font-black text-raad-teal">{(t(company.name_ar, company.name_en) || 'R')[0]}</span>
              )}
            </div>
            <div>
              <SectionTag>{t('شركة ضمن المجموعة', 'Group Company')}</SectionTag>
              <h1 className="mt-4 text-4xl font-black leading-tight text-raad-charcoal sm:text-5xl lg:text-6xl text-balance">
                {t(company.name_ar, company.name_en)}
              </h1>
              <p className="mt-5 max-w-4xl text-lg leading-9 text-raad-graphite/70">
                {t(company.description_ar, company.description_en)}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-10">
            {services.length > 0 && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="focus-line mb-8 text-3xl font-black text-raad-charcoal">
                  {t('خدماتنا', 'Our Services')}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {services.map((service, i) => (
                    <CardShell key={`${service}-${i}`} className="p-5">
                      <div className="relative z-10 flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-raad-teal" />
                        <span className="font-bold text-raad-charcoal">{service}</span>
                      </div>
                    </CardShell>
                  ))}
                </div>
              </motion.section>
            )}

            {company.statistics?.length > 0 && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <h2 className="focus-line mb-8 text-3xl font-black text-raad-charcoal">
                  {t('إحصائيات', 'Statistics')}
                </h2>
                <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
                  {company.statistics.map((stat, i) => (
                    <div key={i} className="rounded-[1.5rem] border border-raad-teal/15 bg-white p-6 text-center shadow-soft">
                      <BarChart3 className="mx-auto mb-3 h-6 w-6 text-raad-teal" />
                      <span className="block text-3xl font-black text-raad-charcoal">{stat.value}</span>
                      <span className="mt-2 block text-sm font-bold text-raad-graphite/60">{t(stat.label_ar, stat.label_en)}</span>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {company.branches?.length > 0 && (
              <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <h2 className="focus-line mb-8 text-3xl font-black text-raad-charcoal">
                  {t('فروعنا', 'Our Branches')}
                </h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  {company.branches.map((branch, i) => (
                    <CardShell key={i} className="p-6">
                      <div className="relative z-10">
                        <h3 className="text-xl font-black text-raad-charcoal">{t(branch.name_ar, branch.name_en)}</h3>
                        <p className="mt-2 text-sm leading-7 text-raad-graphite/65">{t(branch.address_ar, branch.address_en)}</p>
                      </div>
                    </CardShell>
                  ))}
                </div>
              </motion.section>
            )}
          </div>

          <motion.aside initial={{ opacity: 0, x: isRTL ? -30 : 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
            <div className="sticky top-32 rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft">
              <h3 className="text-2xl font-black text-raad-charcoal">
                {t('معلومات التواصل', 'Contact Info')}
              </h3>

              <div className="mt-6 space-y-4 text-sm font-semibold text-raad-graphite/70">
                {company.contact_email && (
                  <a href={`mailto:${company.contact_email}`} className="flex items-center gap-3 hover:text-raad-teal">
                    <Mail className="h-5 w-5 text-raad-teal" />
                    {company.contact_email}
                  </a>
                )}

                {company.contact_phone && (
                  <a href={`tel:${company.contact_phone}`} className="flex items-center gap-3 hover:text-raad-teal">
                    <Phone className="h-5 w-5 text-raad-teal" />
                    <PhoneText>{company.contact_phone}</PhoneText>
                  </a>
                )}

                {(company.address_ar || company.address_en) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-raad-teal" />
                    {t(company.address_ar, company.address_en)}
                  </div>
                )}
              </div>

              <Button asChild className="mt-7 w-full rounded-full bg-raad-charcoal text-white hover:bg-raad-teal">
                <Link to="/contact">{t('تواصل معنا', 'Contact Us')}</Link>
              </Button>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
};

export default CompanyDetailPage;
